import React, { useEffect } from 'react';
import { View, Pressable, LayoutChangeEvent } from 'react-native';
import Modal from '@photosi/rn-components/modals/modal';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../../contexts/StoreContext';
import { TestIDs } from 'src/constants/testIDs.constants';
import { styles } from './ModalEditProducts.styles';
import { Product } from '../../../../screen/picture_view/components/Product';
import { SliderZoom } from '../../../tools/slider-zoom/SliderZoom';
import { SliderRotation } from '../../../tools/slider-rotation/SliderRotation';
import { Icon } from '../../../icon/Icon';
import { ICON_NAMES } from '../../../icon/icon.helper';
import TextLato from '../../../text/text-lato/TextLato';
import { brand, neutrals } from 'src/theme/colors';
import { ACTIVE_TOOLS } from 'src/store/page-project/pageProject.constants';
import { withActiveProduct } from 'src/hoc/withActiveProduct';
import { ProductModelInstance } from 'src/store/_models/product';
import { TOAST_IDS } from '../../../toast/Toast.constants';
import { DynamicModalProps } from '../DynamicModal.types';
import { EditLookupToolbar } from '@photosi/rn-components/toolbars/edit-lookup-toolbar/EditLookupToolbar';
import { CircularButton } from '@photosi/rn-components/buttons/circular-button/CircularButton';
import {
  CIRCULAR_BUTTON_SIZES,
  CIRCULAR_BUTTON_TYPES,
} from '@photosi/rn-components/buttons/circular-button/CircularButton.constants';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { BottomBarEdit } from '@photosi/rn-components/bottom-bar-edit/BottomBarEdit';
import { parseAnalyticsParamsByUserImageArea } from 'src/_shared/analytics.utils';

const ModalEditProductsComponent = observer(
  ({
    type,
    activeProduct,
    dynamicModalVisible,
    dynamicModalLayer: { id },
  }: { activeProduct: ProductModelInstance } & DynamicModalProps) => {
    const {
      i18n,
      project,
      tutorial: { setTutorialArea },
      dynamicModal: { closeModal: _closeModal },
      toolbarDesktop: { setIsToolbarOpen },
      toastStore: { removeToast },
    } = useStore();

    const { isDesktop } = useResponsiveScreen();

    const canUndo = !!project.activeUserImageArea?.userImage?.canUndo;
    const canRedo = !!project.activeUserImageArea?.userImage?.canRedo;

    const closeModal = () => {
      project.setActiveTool(ACTIVE_TOOLS.frame);
      setIsToolbarOpen(false);
      _closeModal();
      project.activePreview?.unloadAll(); // unloads imageEditor
    };

    const onPressUndo = async () => {
      canUndo && project.activeUserImageArea?.userImage?.undo();
      amplitude.trackEvent(
        AnalyticsEvents.modifyPhoto.undoClick,
        parseAnalyticsParamsByUserImageArea(project.activeUserImageArea),
      );
    };

    const onPressRedo = async () => {
      canRedo && project.activeUserImageArea?.userImage?.redo();
      amplitude.trackEvent(
        AnalyticsEvents.modifyPhoto.redoClick,
        parseAnalyticsParamsByUserImageArea(project.activeUserImageArea),
      );
    };

    const onPressFlipHorizontal = async () => {
      project.activeUserImageArea?.userImage?.flipHorizontal();
      amplitude.trackEvent(AnalyticsEvents.modifyPhoto.flipImage, {
        ...parseAnalyticsParamsByUserImageArea(project.activeUserImageArea),
        type: 'horizontal',
      });
    };

    const onPressFlipVertical = async () => {
      project.activeUserImageArea?.userImage?.flipVertical();
      amplitude.trackEvent(AnalyticsEvents.modifyPhoto.flipImage, {
        ...parseAnalyticsParamsByUserImageArea(project.activeUserImageArea),
        type: 'vertical',
      });
    };

    const handleClose = async () => {
      if (project.activeProject) {
        const userImageArea = project.activeProject.userImageAreasWithCropAlert.find(userImageArea => {
          return userImageArea.productId === activeProduct.id;
        });
        if (userImageArea?.userImage?.id) {
          project.activeProject.deactivateCropAlert([userImageArea.userImage.id]).then(() => {
            if (!project.activeProject?.hasAnyImageWithCropAlert) {
              removeToast(TOAST_IDS.CutsCheckAlert);
            }
          });
        }
      }
      amplitude.trackEvent(
        AnalyticsEvents.modifyPhoto.saveClick,
        parseAnalyticsParamsByUserImageArea(project.activeUserImageArea),
      );
      project.goBack();
      closeModal();
    };

    // const handleLayout = (event: LayoutChangeEvent) => {
    //   'worklet';
    //   const { width, height, top, left } = event.nativeEvent.layout;

    //   // setting highlight zone for tutorial
    //   setTutorialArea({
    //     key: 'sliderZoomArea',
    //     value: {
    //       width,
    //       height,
    //       x: left,
    //       y: top,
    //       borderRadius: 24,
    //     },
    //   });
    // };

    useEffect(() => {
      project.activeGroup?.checkToastNotifications();
      return () => {
        project.activeGroup?.checkToastNotifications();
      };
    }, []);

    return (
      <Modal
        style={styles.modalContainer}
        style_container={styles.modalContent}
        showHeader={false}
        isInstant={true}
        visible={dynamicModalVisible}
        testID={TestIDs.EDIT_PRODUCTS}
        onClose={handleClose}
        closeOnClickOutside={false}
        type={type}
        id={id}
      >
        <View style={styles.contentWrapper}>
          {/* Header */}
          {isDesktop && (
            <View style={styles.header}>
              <Pressable
                style={styles.backButton}
                onPress={handleClose}
                accessible={true}
                accessibilityLabel={i18n.t('actions:save_and_return')}
                accessibilityRole="button"
              >
                <Icon name={ICON_NAMES.ArrowLeft2} width={16} height={16} color={brand.main.water} />
                <TextLato style={styles.backButtonText}>{i18n.t('actions:save_and_return')}</TextLato>
              </Pressable>

              <View style={styles.headerRight}>
                <CircularButton
                  onPress={() => onPressUndo()}
                  size={CIRCULAR_BUTTON_SIZES.LARGE_COZY}
                  type={CIRCULAR_BUTTON_TYPES.FILL}
                  iconName={ICON_NAMES.Undo}
                  focusGroup={FocusGroups.MODAL}
                  testID={modalTestID(TestIDs.EDIT_PRODUCTS, TestIDs.UNDO)}
                  focusId={modalTestID(TestIDs.EDIT_PRODUCTS, TestIDs.UNDO)}
                  focusOrder={9}
                  disabled={!canUndo}
                />
                <CircularButton
                  onPress={() => onPressRedo()}
                  size={CIRCULAR_BUTTON_SIZES.LARGE_COZY}
                  type={CIRCULAR_BUTTON_TYPES.FILL}
                  iconName={ICON_NAMES.Redo}
                  focusGroup={FocusGroups.MODAL}
                  testID={modalTestID(TestIDs.EDIT_PRODUCTS, TestIDs.REDO)}
                  focusId={modalTestID(TestIDs.EDIT_PRODUCTS, TestIDs.REDO)}
                  focusOrder={10}
                  disabled={!canRedo}
                />
              </View>
            </View>
          )}
          {!isDesktop && (
            <View style={styles.headerMobile}>
              <TextLato style={styles.headerTitleMobile}>{i18n.t('edit:edit-photo')}</TextLato>
            </View>
          )}

          {/* Main Content */}
          <View style={styles.mainContent}>
            <View style={styles.productContainer}>
              {/* Zoom slider with icons */}
              {isDesktop && (
                <View style={styles.topToolsContainer}>
                  <View style={styles.sliderZoomContainer}>
                    <View style={styles.sliderContainer}>
                      <SliderZoom isDark />
                    </View>
                    <View style={styles.divider} />
                    <View style={styles.rightIconsContainer}>
                      <Pressable style={styles.iconButtonWrapper} onPress={() => onPressFlipVertical()}>
                        <View style={{ width: 24, height: 24 }}>
                          <Icon name={ICON_NAMES.FlipVertical} color={neutrals.neutralGrays.graphite} />
                        </View>
                      </Pressable>
                      <Pressable style={styles.iconButtonWrapper} onPress={() => onPressFlipHorizontal()}>
                        <View style={{ width: 24, height: 24 }}>
                          <Icon name={ICON_NAMES.FlipHorizontal} color={neutrals.neutralGrays.graphite} />
                        </View>
                      </Pressable>
                    </View>
                  </View>
                </View>
              )}

              {/* Product Preview */}
              <View style={isDesktop ? styles.productWrapper : styles.productWrapperMobile}>
                <Product
                  product={activeProduct}
                  id={`modal-product-${activeProduct.id}`}
                  index={0}
                  shouldShowPreviewUtilsBar={false}
                  shouldUseContainerDimensions={true}
                  isModalEdit
                />
              </View>

              {/* Rotation slider */}
              {isDesktop && (
                <View style={styles.bottomToolsContainer}>
                  <SliderRotation />
                </View>
              )}
            </View>
          </View>
        </View>
        {!isDesktop && (
          <>
            <BottomBarEdit activeProduct={activeProduct} />
            <View style={styles.bottomBarPlaceholder} />
          </>
        )}

        {isDesktop && <EditLookupToolbar activeProduct={activeProduct} />}
      </Modal>
    );
  },
);

export const ModalEditProducts = withActiveProduct(ModalEditProductsComponent);

ModalEditProducts.displayName = 'ModalEditProducts';
